/**
 * Backend contract types for the platform-api auth + identity surface.
 * Shapes match the JWT issued by apps/platform-api auth.service and the
 * response envelopes from /auth/*, /me, /workspaces, /invites endpoints.
 */

export type WorkspaceRole = 'OWNER' | 'ADMIN' | 'MEMBER';
export type AccessStatus = 'ACTIVE' | 'PAUSED' | 'SUSPENDED';

/**
 * An app's platform-wide availability, owned by the App registry (/admin →
 * App registry). Orthogonal to AccessStatus: AccessStatus says whether THIS
 * member may use the app in THIS workspace; AppRegistryStatus says whether the
 * app is shipped at all. An app that is COMING_SOON or DEPRECATED is not
 * launchable anywhere, however a workspace has it provisioned.
 */
export type AppRegistryStatus = 'ACTIVE' | 'BETA' | 'DEPRECATED' | 'COMING_SOON';

export interface JwtAppAccessEntry {
  role: string;
  status: AccessStatus;
  reason?: string;
}

/**
 * Per-app entry in /me's `apps` / `active_workspace_apps` maps. Combines the
 * user's effective access (role + status) with the app-registry metadata
 * (name/description/icon/version/registry_status), so the shell renders the
 * AppSwitcher, launcher, and route guards straight from the backend registry
 * instead of hardcoding per-app labels. The metadata fields are optional so a
 * response from an older backend (access-only map) still typechecks.
 */
export interface ActiveAppEntry {
  role: string;
  status: AccessStatus;
  /** Display name from the App registry (e.g. "Projects"). */
  name?: string;
  /** One-line blurb from the App registry. */
  description?: string | null;
  /** Icon key from the App registry (e.g. "briefcase"). */
  icon?: string | null;
  /** Browser-tab favicon path from the App registry (e.g. "/favicons/projects.svg"). */
  favicon?: string | null;
  /** Brand accent color (hex) from the App registry. */
  brand_color?: string | null;
  version?: string;
  /** Registry lifecycle status — gates launchability (only ACTIVE is shown). */
  registry_status?: AppRegistryStatus;
  /**
   * AppModule codes this workspace is NOT entitled to, set by a Sarvadhi
   * platform admin. Rail items and settings tabs declaring one of these codes
   * are hidden.
   *
   * A DENY-list, deliberately. Empty — or absent, on a backend that predates
   * this field — must hide NOTHING, so a transitional deploy or an app whose
   * module registry is not seeded can never blank a tenant's navigation. The
   * backend gate is authoritative either way; this only spares the user a
   * dead-end click.
   */
  disabled_modules?: string[];
}

/** Decoded JWT claims. */
export interface JwtPayload {
  sub: number;
  email: string;
  roles?: string[];
  permissions?: string[];

  workspace_id?: string;
  workspace_slug?: string;
  workspace_role?: WorkspaceRole;
  app_access?: Record<string, JwtAppAccessEntry>;

  iat: number;
  exp: number;
}

export interface AuthenticatedUser {
  id: number;
  email: string;
  name: string;
  phone?: string | null;
  created_at?: string;
}

/**
 * POST /auth/signup — step 1 of self-serve workspace signup. ACCOUNTLESS: no
 * name/password here — those are collected later, in the same request as the
 * org+workspace details, once the owner account is created atomically inside
 * the new tenant database.
 */
export interface SignupRequest {
  email: string;
}

export interface SignupResponse {
  email: string;
  otp_sent: boolean;
  otp_expires_in_seconds: number;
}

export interface VerifyOtpRequest {
  email: string;
  code: string;
}

/**
 * POST /auth/verify-otp response. NOT a session — no access_token, no user,
 * no next_step, no cookie. `email_verification_token` proves this email was
 * OTP-verified; carry it into POST /workspaces (self-serve creation) as
 * `email_verification_token`, matching `owner_email`.
 */
export interface VerifyOtpResponse {
  email: string;
  email_verification_token: string;
  expires_in_seconds: number;
}

export interface LoginRequest {
  email: string;
  password: string;
  /**
   * REQUIRED. Login is workspace-scoped: `public.User` lives inside each
   * workspace's own database, so without a slug there is no database to
   * authenticate against. The backend returns 400 WORKSPACE_REQUIRED.
   * Supplied by the subdomain (or the session in single-host mode), never
   * typed on the sign-in form.
   */
  workspace_slug: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: 'Bearer';
  user: AuthenticatedUser;
  workspace: { id: string; slug: string; role: WorkspaceRole } | null;
  next_step: 'CREATE_WORKSPACE_OR_ACCEPT_INVITE' | 'ROUTE_TO_WORKSPACE';
  /** True when 2FA is required for this user but not yet enrolled (force setup). */
  must_enrol_2fa?: boolean;
}

/**
 * Alternative /auth/login response when the account has 2FA enabled: no token is
 * issued; redeem `challenge_token` at POST /auth/2fa/verify-login with a code.
 */
export interface TwoFactorChallengeResponse {
  two_factor_required: true;
  challenge_token: string;
  user: AuthenticatedUser;
}

/**
 * Alternative /auth/login response parking the user on 2FA enrolment. Only a
 * LIMITED enrolment token is issued (no workspace access). Returned identically
 * whether the user authenticated with a password or an email code.
 *
 * Two cases, distinguished by `can_skip`:
 *   - COMPULSORY (`can_skip: false`) — the workspace's own 2FA policy requires it,
 *     or the account is a platform admin / forced workspace OWNER. The client must
 *     enrol and then call POST /auth/2fa/complete-enrolment for the real session.
 *   - OPTIONAL one-time offer (`can_skip: true`) — 2FA is not required here; the
 *     user has simply never been asked. The client may offer "Skip for now", which
 *     calls POST /auth/2fa/skip-enrolment to dismiss it permanently and return a
 *     full session.
 */
export interface Enrol2faRequiredResponse {
  enrol_2fa_required: true;
  access_token: string;
  token_type: 'Bearer';
  user: AuthenticatedUser;
  /** Where to bind the session after enrolment (null = onboarding). */
  workspace_slug: string | null;
  /**
   * Whether "Skip for now" is allowed. The server re-checks this on
   * /auth/2fa/skip-enrolment and refuses when false — never trust it as the only
   * enforcement. Optional for back-compat with older API builds; treat a missing
   * value as false (compulsory), which fails closed.
   */
  can_skip?: boolean;
}

/**
 * Alternative /auth/login (and /auth/2fa/verify-login) response: the verified
 * password no longer meets the effective policy, or has aged past rotation_days.
 * Only a LIMITED token is issued (no workspace access) — good only for
 * POST /auth/change-password. The client forces a change, then signs in again
 * with the new password. For 2FA accounts this is returned by verify-login, so a
 * forced change never bypasses 2FA.
 */
export interface PasswordChangeRequiredResponse {
  password_change_required: true;
  /** POLICY = fails current rules; ROTATION = older than rotation_days. */
  reason: 'POLICY' | 'ROTATION';
  access_token: string;
  token_type: 'Bearer';
  /** Where to bind the session after the change + re-login (null = onboarding). */
  workspace_slug: string | null;
  user: AuthenticatedUser;
}

/**
 * Alternative /auth/login (and /auth/2fa/verify-login) response: an admin has
 * force-flagged this account for a password reset. No token is issued — the user
 * must complete the existing forgot-password OTP flow to regain access. Issued
 * AFTER 2FA verification (if the account has 2FA) so the reset gate never
 * bypasses two-factor auth.
 */
export interface PasswordResetRequiredResponse {
  password_reset_required: true;
  user: AuthenticatedUser;
}

/**
 * /auth/login returns a full session, a 2FA challenge, a forced-2FA-enrolment
 * session, a forced-password-change gate, or a forced-reset gate.
 */
export type LoginResult =
  | LoginResponse
  | TwoFactorChallengeResponse
  | Enrol2faRequiredResponse
  | PasswordChangeRequiredResponse
  | PasswordResetRequiredResponse;

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallengeResponse {
  return (r as TwoFactorChallengeResponse).two_factor_required === true;
}

export function isEnrol2faRequired(r: LoginResult): r is Enrol2faRequiredResponse {
  return (r as Enrol2faRequiredResponse).enrol_2fa_required === true;
}

export function isPasswordChangeRequired(r: LoginResult): r is PasswordChangeRequiredResponse {
  return (r as PasswordChangeRequiredResponse).password_change_required === true;
}

export function isPasswordResetRequired(r: LoginResult): r is PasswordResetRequiredResponse {
  return (r as PasswordResetRequiredResponse).password_reset_required === true;
}

/** POST /auth/change-password body. `token` is client-only (not sent in the JSON body). */
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  workspace_slug?: string;
}

/** POST /auth/2fa/complete-enrolment body. */
export interface CompleteEnrolmentRequest {
  workspace_slug?: string;
}

/** POST /auth/2fa/verify-login body. */
export interface VerifyLoginTwoFactorRequest {
  challenge_token: string;
  code: string;
  workspace_slug?: string;
}

/** POST /auth/2fa/setup response. */
export interface TwoFactorSetupResponse {
  secret: string;
  otpauth_url: string;
}

/** POST /auth/2fa/enable body + response. */
export interface EnableTwoFactorRequest {
  code: string;
}
export interface EnableTwoFactorResponse {
  backup_codes: string[];
}

/** POST /auth/2fa/disable body. */
export interface DisableTwoFactorRequest {
  code: string;
}

/** GET /auth/2fa/status response. */
export interface TwoFactorStatusResponse {
  enabled: boolean;
  enrolled_at: string | null;
  backup_codes_remaining: number;
}

/** POST /auth/request-login-otp body. */
export interface RequestLoginOtpRequest {
  email: string;
  /**
   * OPTIONAL, unlike LoginWithOtpRequest's — the apex "email code" tab has no
   * workspace yet. Once given, the account is checked EXCLUSIVELY on that
   * workspace's own database, no fallback to the shared table. Send '' and
   * the backend 400s (min length 3) — omit the key entirely when the slug
   * isn't known yet.
   */
  workspace_slug?: string;
}

/** POST /auth/request-login-otp response. Same shape regardless of email validity (no enumeration). */
export interface RequestLoginOtpResponse {
  otp_sent: true;
  otp_expires_in_seconds: number;
}

/** POST /auth/login-with-otp body. */
export interface LoginWithOtpRequest {
  email: string;
  code: string;
  /**
   * REQUIRED. Login is workspace-scoped: `public.User` lives inside each
   * workspace's own database, so without a slug there is no database to
   * authenticate against. The backend returns 400 WORKSPACE_REQUIRED.
   * Supplied by the subdomain (or the session in single-host mode), never
   * typed on the sign-in form.
   */
  workspace_slug: string;
}

/** POST /auth/login-with-otp response. Mirrors LoginResponse. */
/**
 * /auth/login-with-otp returns a full session OR — for a 2FA-enrolled account —
 * a challenge (same as password login), so an enrolled user never bypasses 2FA
 * by signing in with a code.
 */
export type LoginWithOtpResponse = LoginResult;

export interface SwitchWorkspaceRequest {
  workspace_id?: string;
  workspace_slug?: string;
}

export interface SwitchWorkspaceResponse {
  access_token: string;
  token_type: 'Bearer';
  workspace: { id: string; slug: string; name: string };
  role: WorkspaceRole;
}

/** GET /me — frontend bootstrap response. */
export interface MeResponse {
  user: AuthenticatedUser;
  workspaces: MeWorkspace[];
  active_workspace_slug: string | null;
  active_workspace_apps: Record<string, ActiveAppEntry>;
  next_step: 'CREATE_WORKSPACE_OR_ACCEPT_INVITE' | 'GO_TO_WORKSPACE';
}

export interface MeWorkspace {
  id: string;
  slug: string;
  name: string;
  logo_url?: string | null;
  brand_color?: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  is_owned: boolean;
  role: WorkspaceRole;
  /**
   * Admin-console capability keys held by an ADMIN member (manage_settings,
   * manage_members, …). OWNER implicitly holds all — do not treat an empty
   * array as "no access" for owners. Independent of per-app roles in `apps`.
   */
  admin_permissions?: string[];
  membership_status: AccessStatus;
  apps: Record<string, ActiveAppEntry>;
  /**
   * Onboarding wizard state for this workspace. DONE means the workspace is
   * past the wizard; any other value means the owner still needs to finish
   * setup. Resume logic (spec §8) uses this to route returning users.
   * Made optional so older clients tolerate a missing field; backend will
   * always populate it after the OB1-2 migration.
   */
  onboarding_state?: 'ORG_DETAILS' | 'LOCALE' | 'BRANDING' | 'DONE';
}
